import { useState } from 'react';
import { apiKeyCreate, apiKeyRevoke, apiKeys, governedSettings, governedStatus, tenantKeyRotate, type ApiError, type TenantSettings } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, btn, pre, sx, useApi, useGuarded, usePage } from './kit';

const POSTURES: [NonNullable<TenantSettings['gate']['defaultPosture']>, string][] = [['ESCALATE', '· fail-safe'], ['HOLD', '· wait for a human'], ['BLOCK', '· refuse and seal']];
const CAPT6 = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;';

/** Security & Keys (design seIsKeys, T9204; L22134-22158 and seGuard L21693-21694). */
export function KeysSection() {
  const { client } = useSession();
  const { ask, changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const status = useApi((c, s) => governedStatus(c, { signal: s }), []);
  const settings = useApi((c, s) => governedSettings(c, { signal: s }), []);
  const keys = useApi((c, s) => apiKeys(c, { signal: s }), []);
  const [liveKey, setLiveKey] = useState<string | null>(null);
  const [fresh, setFresh] = useState<Record<string, string>>({});
  const [asking, setAsking] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);

  const posture = ((settings.data?.settings as TenantSettings | undefined)?.gate?.defaultPosture) ?? null;
  const pIdx = POSTURES.findIndex((p) => p[0] === posture);
  const postureReason = settings.data?.defaultPostureReason ?? null;
  const list = keys.data?.keys ?? [];
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  return (
    <div data-testid="settings-keys" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:820px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Security &amp; keys</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:16px;')}>Authenticates inbound gate requests. Rotating invalidates the old key immediately.</div>
      <div style={sx(CAPT6)}>Live key</div>
      <div style={sx('display:flex;align-items:center;height:38px;padding:0 4px 0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;margin-bottom:15px;')}>
        <span data-testid="live-key" style={sx(liveKey ? 'font-variant-numeric:tabular-nums;font-size:16px;color:#22272E;' : 'font-size:16px;color:#54606C;')}>{I(liveKey ?? 'Not available')}</span>
        <span style={sx('flex:1;')} />
        <a
          href="#"
          {...wr(() => {
            if (liveKey || !status.data) return;
            ask({
              sec: 'Security & Keys', title: 'Rotate the live key?',
              body: 'The old key stops working immediately. Any integration still using it fails on its next call until it gets the new key.', btn: 'Rotate key',
              go: async () => {
                const r = await tenantKeyRotate(client, { params: { id: status.data!.tenant.id }, body: { confirm: true } });
                setLiveKey(r.apiKey);
              },
            });
          })}
          style={sx('display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:6px;font-size:14px;font-weight:700;letter-spacing:.07em;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (liveKey ? 'background:rgba(47,138,111,.16);color:#12503C;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'))}
        >
          {I(liveKey ? 'ROTATED' : 'ROTATE')}
        </a>
      </div>
      <div style={sx(CAPT6)}>Default posture</div>
      {/* The gate acts on no posture choice yet, so this is shown as not active and is not
          offered as a change: a click here would claim a behaviour that does not happen. */}
      <div
        data-testid="default-posture"
        aria-label={'Default posture: ' + (pIdx >= 0 ? POSTURES[pIdx][0] : 'not active yet')}
        title={postureReason ?? undefined}
        style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:4px;min-height:38px;padding:6px 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;margin-bottom:15px;box-sizing:border-box;')}
      >
        <span style={sx('font-size:16px;font-weight:700;color:#54606C;')}>{I(pIdx >= 0 ? POSTURES[pIdx][0] + ' ' + POSTURES[pIdx][1] : settings.data ? 'Not active yet' : '–')}</span>
        {postureReason && <span style={sx('font-size:16px;color:#54606C;line-height:1.45;flex:1 1 260px;min-width:0;')}>{I(postureReason)}</span>}
      </div>
      <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:9px;')}>
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Programmatic keys</span>
        <span style={sx('flex:1;')} />
        <a
          href="#"
          {...wr(() => void run(async () => {
            const r = await apiKeyCreate(client, { body: { name: 'Console key ' + (list.length + 1) } });
            setFresh((p) => ({ ...p, [r.key.id]: r.secretKey }));
            keys.reload();
          }))}
          style={sx(btn('ghost'))}
        >
          + Create
        </a>
      </div>
      {list.map((k) => (
        <div key={k.id} style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:10px 12px;border-radius:9px;background:#FBF8F1;border:1px solid rgba(34,39,46,.11);margin-bottom:7px;')}>
          <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#22272E;')}>{I(fresh[k.id] ?? k.keyPrefix)}</span>
          <span style={sx('font-size:16px;color:#54606C;')}>
            {I(fresh[k.id] ? 'created just now · shown once' : k.name + ' · created ' + new Date(k.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))}
          </span>
          <span style={sx('flex:1;')} />
          <a href="#" className="se-hover-dark" aria-disabled={!canWrite || undefined} title={roReason ?? undefined} onClick={(e) => { pre(e); if (canWrite) setAsking(asking === k.id ? null : k.id); }} style={sx('font-size:16px;color:#B3261E;text-decoration:none;')}>
            Revoke
          </a>
          {asking === k.id && (
            <span style={sx('flex:1 1 100%;display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding-top:8px;border-top:1px solid rgba(179,38,30,.18);')}>
              <span style={sx('flex:1 1 260px;font-size:16px;color:#22272E;line-height:1.45;')}>
                {'Revokes '}
                <b>{I(fresh[k.id] ?? k.keyPrefix)}</b>
                {'. Anything calling the API with it fails on its next request. This can’t be undone; a new key has to be created and redeployed.'}
              </span>
              <a
                href="#"
                onClick={(e) => {
                  pre(e);
                  void run(async () => {
                    await apiKeyRevoke(client, { params: { keyId: k.id }, query: { confirm: true } });
                    setAsking(null);
                    keys.reload();
                  });
                }}
                style={sx('display:inline-flex;align-items:center;min-height:34px;padding:0 13px;border-radius:8px;background:#B3261E;color:#FFFFFF;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
              >
                Revoke key
              </a>
              <a href="#" onClick={(e) => { pre(e); setAsking(null); }} style={sx('font-size:16px;font-weight:700;color:#54606C;text-decoration:none;white-space:nowrap;')}>Keep it</a>
            </span>
          )}
        </div>
      ))}
      {keys.data && list.length === 0 && (
        <div style={sx('display:flex;align-items:center;height:44px;padding:0 14px;border-radius:9px;background:#F7F3EA;border:1px solid rgba(34,39,46,.1);font-size:16px;color:#54606C;')}>No API keys yet</div>
      )}
      {keys.error && <ErrLine error={keys.error} />}
      <ErrLine error={err} />
      <div style={sx('font-size:16px;color:#54606C;margin-top:12px;line-height:1.55;')}>
        {I('A key is shown once at creation and stored hashed. Rotating the live key invalidates the old one immediately, so any integration still holding it fails closed rather than silently degrading.')}
      </div>
    </div>
  );
}
